// src/app/modules/Crewing/auth/AuthProvider.tsx
import React, { FC, useState, useEffect, createContext, useContext, Dispatch, SetStateAction, useCallback, useMemo } from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, ProfileRoleDetails, ProfileTenant, ResolvedPermission, UserModel } from './_models'
import * as authHelper from './AuthHelpers'
import axios from 'axios'
import { WithChildren } from '../../../../_metronic/helpers'
import { getUserByToken } from './_requests'
import type { MenuTreeNode } from '../../../shared/types/menu-tree'
import { hasAllPermissions, hasAnyPermission, hasPermission } from './permissionUtils'
import { normalizeProfilePayload, resolveIsSuperAdmin } from './profileNormalization'

const API_URL = process.env.REACT_APP_API_URL

// 1) Context shape: only auth, saveAuth, logout
type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  loadProfile: () => Promise<UserModel | undefined>
  logout: () => void
  permissions: Set<string>
  resolvedPermissions: Record<string, ResolvedPermission>
  permissionsLoaded: boolean
  menuTree?: MenuTreeNode[]
  roleDetails?: ProfileRoleDetails | null
  tenant?: ProfileTenant | null
  can: (perm: string) => boolean
  /** Super-admin (roleType), independent of numeric role id. */
  isSuperAdmin: boolean
  hasPermission: (code: string) => boolean
  hasAnyPermission: (codes: string[]) => boolean
  hasAllPermissions: (codes: string[]) => boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  loadProfile: async () => undefined,
  logout: () => { },
  permissions: new Set<string>(),
  resolvedPermissions: {},
  permissionsLoaded: false,
  menuTree: undefined,
  roleDetails: null,
  tenant: null,
  can: () => false,
  isSuperAdmin: false,
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
}

const computeCompanyUsernameFromUser = (u?: UserModel): string | undefined => {
  if (!u?.role?.id) return undefined
  const roleId = u.role.id

  const cgaUsername =
    (u as any)?.companyGroupAdmin?.username ??
    (u as any)?.vessel?.companyGroupAdmin?.username

  const caUsername =
    (u as any)?.companyAdmin?.username ??
    (u as any)?.vessel?.companyAdmin?.username

  switch (roleId) {
    case 5:
      return cgaUsername
    case 2:
      return cgaUsername
    case 3:
      return cgaUsername
    case 4:
      return cgaUsername
    case 11:
      return cgaUsername ?? caUsername ?? undefined
    default:
      return undefined
  }
}


const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)
export const useAuth = () => useContext(AuthContext)

export const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const [resolvedPermissions, setResolvedPermissions] = useState<Record<string, ResolvedPermission>>({})
  const [permissions, setPermissions] = useState<Set<string>>(new Set())
  const [permissionsLoaded, setPermissionsLoaded] = useState(false)
  const [roleDetails, setRoleDetails] = useState<ProfileRoleDetails | null>(null)
  const [tenant, setTenant] = useState<ProfileTenant | null>(null)
  const isSuperAdmin = useMemo(() => resolveIsSuperAdmin(currentUser, roleDetails), [currentUser, roleDetails])

  const saveAuth = useCallback((newAuth: AuthModel | undefined) => {
    const authWithoutTree = newAuth ? { ...newAuth, menuTree: undefined } : undefined
    setAuth(authWithoutTree)
    if (authWithoutTree) {
      authHelper.setAuth(authWithoutTree)
      const roleId = authWithoutTree.role.id
      sessionStorage.setItem('lastRoleId', String(roleId))
    } else {
      authHelper.removeAuth()
      sessionStorage.clear()
    }
  }, [])

  const logout = useCallback(async () => {
    const companyUsername = sessionStorage.getItem('companyUsername');
    const lastRoleId = sessionStorage.getItem('lastRoleId');

    try {
      await axios.post(`${API_URL}/auth/logout`);
    } catch (err) {
      console.error('Logout API failed', err);
    }

    saveAuth(undefined);
    setCurrentUser(undefined);
    setRoleDetails(null);
    setTenant(null);
    setResolvedPermissions({});
    setPermissions(new Set());
    setPermissionsLoaded(false);

    let target = '/auth';
    if (companyUsername) {
      target = `/auth/${companyUsername}`;
      if (lastRoleId === '4') target += '/ship-login';
    }

    window.location.replace(target);
  }, [saveAuth]);

  const loadProfile = useCallback(async () => {
    if (!auth?.auth?.jwt) {
      setCurrentUser(undefined)
      setRoleDetails(null)
      setTenant(null)
      setResolvedPermissions({})
      setPermissions(new Set())
      setPermissionsLoaded(false)
      return
    }

    try {
      const { data } = await getUserByToken()
      const normalized = normalizeProfilePayload(data)
      setCurrentUser(normalized.user)
      setRoleDetails(normalized.roleDetails)
      setTenant(normalized.tenant)
      setResolvedPermissions(normalized.permissions)
      setPermissions(
        new Set(
          Object.entries(normalized.permissions)
            .filter(([, decision]) => decision.allowed === true)
            .map(([code]) => code)
        )
      )
      setPermissionsLoaded(true)
      return normalized.user
    } catch (err: any) {
      console.error('Failed to load /profile/me', err)
      logout()
      return undefined
    }
  }, [auth?.auth?.jwt, logout, normalizeProfilePayload])

  const can = useCallback(
    (perm: string) => isSuperAdmin || hasPermission(resolvedPermissions, perm),
    [isSuperAdmin, resolvedPermissions]
  )

  const hasPermissionCb = useCallback(
    (code: string) => {
      if (isSuperAdmin) return true
      return hasPermission(resolvedPermissions, code)
    },
    [isSuperAdmin, resolvedPermissions]
  )

  const hasAnyPermissionCb = useCallback(
    (codes: string[]) => {
      if (isSuperAdmin) return true
      return hasAnyPermission(resolvedPermissions, codes)
    },
    [isSuperAdmin, resolvedPermissions]
  )

  const hasAllPermissionsCb = useCallback(
    (codes: string[]) => {
      if (isSuperAdmin) return true
      return hasAllPermissions(resolvedPermissions, codes)
    },
    [isSuperAdmin, resolvedPermissions]
  )

  useEffect(() => {
    if (!currentUser) {
      sessionStorage.removeItem('companyUsername');
      return;
    }

    if (currentUser.role?.id) {
      sessionStorage.setItem('lastRoleId', String(currentUser.role.id));
    }

    const derived = computeCompanyUsernameFromUser(currentUser);
    if (derived) {
      sessionStorage.setItem('companyUsername', derived);
    } else {
      const m = window.location.pathname.match(/^\/auth\/([^/]+)(?:\/|$)/);
      if (m && m[1] && !['login', 'forgot-password', 'registration'].includes(m[1])) {
        sessionStorage.setItem('companyUsername', m[1]);
      } else {
        sessionStorage.removeItem('companyUsername');
      }
    }
  }, [currentUser]);

  useEffect(() => {
    const handleRbacRefresh = () => {
      loadProfile().catch((error) => {
        console.error('Failed to refresh /profile/me after RBAC update', error)
      })
    }

    window.addEventListener('rbac-permissions-updated', handleRbacRefresh)
    return () => {
      window.removeEventListener('rbac-permissions-updated', handleRbacRefresh)
    }
  }, [loadProfile])

  useEffect(() => {
    if (!auth?.auth?.jwt) return

    const checkSessionExpiry = () => {
      const currentAuth = authHelper.getAuth()
      if (currentAuth?.auth?.jwt && authHelper.isJwtExpired(currentAuth.auth.jwt)) {
        authHelper.tryRefreshOrLogout().catch(() => logout())
      }
    }

    checkSessionExpiry()

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkSessionExpiry()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', checkSessionExpiry)

    const interval = setInterval(checkSessionExpiry, 30000)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', checkSessionExpiry)
      clearInterval(interval)
    }
  }, [auth?.auth?.jwt, logout])

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        loadProfile,
        logout,
        permissions,
        resolvedPermissions,
        permissionsLoaded,
        menuTree: auth?.menuTree,
        roleDetails,
        tenant,
        can,
        isSuperAdmin,
        hasPermission: hasPermissionCb,
        hasAnyPermission: hasAnyPermissionCb,
        hasAllPermissions: hasAllPermissionsCb,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// AuthInit: wait for auth.jwt presence before hiding splash
export const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, logout, loadProfile } = useAuth()
  const [showSplash, setShowSplash] = useState(true)

  useEffect(() => {
    let alive = true
    const bootstrap = async () => {
      if (!auth?.auth?.jwt) {
        if (alive) setShowSplash(false)
        return
      }
      try {
        await loadProfile()
      } catch (error) {
        console.error('Bootstrap profile load failed', error)
        if (alive) {
          logout()
        }
      } finally {
        if (alive) setShowSplash(false)
      }
    }
    bootstrap()
    return () => {
      alive = false
    }
  }, [auth?.auth?.jwt, loadProfile, logout])

  return showSplash ? <LayoutSplashScreen /> : <>{children}</>
}
